import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    model,
    output,
    signal,
} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {DateTime} from 'luxon';
import {AppValidators, browserTimeZone, TranslatePipe} from '@brunata-angular/util';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {SavedExport} from '../saved-export.model';
import {ActionButtonComponent} from '../../shared/ui/action-button/action-button.component';

export type SavedExportDownloadRequest = {
    readingDate: DateTime;
    timeZone: string;
};

@Component({
    selector: 'app-export-now-modal',
    standalone: true,
    imports: [ReactiveFormsModule, TranslatePipe, InputDateGroupComponent, ActionButtonComponent],
    templateUrl: './export-now-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportNowModalComponent {
    readonly savedExport = model.required<SavedExport>();
    readonly export = output<SavedExportDownloadRequest>();

    protected readonly activeModal = inject(NgbActiveModal);

    protected readonly maxDate = DateTime.now().startOf('day');
    readonly isExporting = signal(false);

    readonly readingDate = new FormControl<DateTime | null>(this.maxDate, {
        validators: [Validators.required, AppValidators.maxDate(this.maxDate)],
    });

    protected readonly windowDays = computed(() => {
        const daysBackInTime = this.savedExport().daysBackInTime;
        return daysBackInTime === null ? null : String(daysBackInTime);
    });

    private readonly timeZone = signal(browserTimeZone());

    protected readonly form = new FormGroup({
        readingDate: this.readingDate,
    });

    constructor() {
        effect(() => {
            const stored = this.savedExport().timeZone;
            if (stored !== null) {
                this.timeZone.set(stored);
            }
        });
    }

    submit(): void {
        const readingDate = this.readingDate.value;
        if (this.readingDate.invalid || readingDate === null) {
            this.readingDate.markAsTouched();
            return;
        }
        this.export.emit({readingDate, timeZone: this.timeZone()});
    }
}
